/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Row,
  Select,
} from 'antd';
import { useMutation } from '@tanstack/react-query';
import { IDataModal } from '@/interfaces/dataModal';
import { IUpdatePaymentReceived } from '@/interfaces/collector';
import locale from 'antd/es/date-picker/locale/th_TH';
import dayjs from 'dayjs';
import { getPaymentReceivedStatusOptions } from '@/helpers/orderHelper';
import { updatePaymentReceived } from '@/adapters/collector';

interface IProps {
  dataModal?: IDataModal<Partial<IUpdatePaymentReceived>>;
  handleOk: () => void;
  handleCancel: () => void;
}

const PaymentReceivedModal = ({
  dataModal,
  handleOk,
  handleCancel,
}: IProps) => {
  const [form] = Form.useForm<IUpdatePaymentReceived>();
  const mutations = useMutation({
    mutationFn: async (data: IUpdatePaymentReceived) =>
      updatePaymentReceived(data),
    mutationKey: ['update-payment'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        notification.success({
          message: 'อัพเดทข้อมูลสำเร็จ',
        });
        handleOk();
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    form
      .validateFields()
      .then(async (values: IUpdatePaymentReceived) => {
        values.id = Number(dataModal?.id || 0);
        await mutations.mutateAsync(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const onReset = () => {
    form.resetFields();
  };

  useEffect(() => {
    if (dataModal?.id) {
      form.setFieldsValue({
        ...dataModal?.item,
        billingDate:
          (dataModal?.item?.billingDate &&
            dayjs(dataModal?.item?.billingDate)) ||
          undefined,
        paidDate:
          (dataModal?.item?.paidDate && dayjs(dataModal?.item?.paidDate)) ||
          undefined,
      });
    }
  }, [dataModal?.id, dataModal?.item, form]);

  return (
    <Modal
      title={'อัพเดทข้อมูล'}
      open={dataModal?.open}
      onOk={() => handleUpdate()}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      footer={[
        <Button key="back" onClick={handleCancel}>
          ยกเลิก
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="productGroup"
          loading={mutations.isPending}
          onClick={handleUpdate}
        >
          บันทึก
        </Button>,
      ]}
      afterClose={onReset}
    >
      <div className="flex w-full">
        <Form
          form={form}
          name="paymentReceived"
          onFinish={handleUpdate}
          layout="vertical"
          className="w-full"
        >
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item<IUpdatePaymentReceived>
                label="เลขที่ Invoice"
                name="ivNo"
              >
                <Input size="large" readOnly />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item<IUpdatePaymentReceived>
                label="Billing No."
                name="billingNo"
              >
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item<IUpdatePaymentReceived>
                label="Cheque No."
                name="chequeNo"
              >
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<IUpdatePaymentReceived>
                label="วันที่วางบิล"
                name="billingDate"
                rules={[{ required: true, message: 'ระบุวันที่วันที่วางบิล' }]}
              >
                <DatePicker size="large" locale={locale} className="w-full" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<IUpdatePaymentReceived>
                label="วันที่รับเงิน"
                name="paidDate"
              >
                <DatePicker size="large" locale={locale} className="w-full" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<IUpdatePaymentReceived>
                label="จำนวนเงินที่รับ"
                name="paidAmount"
              >
                <InputNumber
                  size="large"
                  className="w-full"
                  placeholder="จำนวนเงินที่รับ"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item<IUpdatePaymentReceived> label="สถานะ" name="status">
                <Select
                  allowClear
                  className="md:min-w-[150px]"
                  size="large"
                  placeholder="เลือก สถานะ"
                  options={getPaymentReceivedStatusOptions()}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="หมายเหตุ" name="remark">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

export default PaymentReceivedModal;
